import { useEffect, useMemo, useState, useCallback } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  Loader2,
  ShieldAlert,
  RotateCcw,
  Download,
  ArrowLeft,
  Rocket,
  PartyPopper,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { supabase } from "@/integrations/supabase/client";
import { getIsAdmin } from "@/lib/reports.functions";
import {
  getDeployChecklist,
  setDeployCheck,
  resetDeployChecklist,
  recordLaunch,
  getLaunchedAt,
  type DeployItemStatus,
} from "@/lib/deploy-checklist.functions";

export const Route = createFileRoute("/_authenticated/admin_/deploy-checklist")({
  head: () => ({
    meta: [
      { title: "デプロイチェックリスト | 管理コンソール" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DeployChecklistPage,
});

const CATEGORY_LABELS: Record<string, string> = {
  X: "X. ドメイン設定",
  Y: "Y. 環境変数の本番値切替",
  Z: "Z. Stripe本番モード",
  AA: "AA. Supabase",
  BB: "BB. 監視",
  CC: "CC. SEO登録",
  DD: "DD. SNS / マーケ準備",
  EE: "EE. サポート体制",
  FF: "FF. 法務最終確認",
  GG: "GG. ローンチ前最終",
};

const FINAL_GATE_IDS = ["GG_01", "GG_02", "GG_03", "GG_04", "GG_05", "GG_06"];

function DeployChecklistPage() {
  const isAdminFn = useServerFn(getIsAdmin);
  const getListFn = useServerFn(getDeployChecklist);
  const setCheckFn = useServerFn(setDeployCheck);
  const resetFn = useServerFn(resetDeployChecklist);
  const recordLaunchFn = useServerFn(recordLaunch);
  const getLaunchedAtFn = useServerFn(getLaunchedAt);

  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  const [items, setItems] = useState<DeployItemStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [activeCat, setActiveCat] = useState<string>("X");
  const [onlyUnchecked, setOnlyUnchecked] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [launchedAt, setLaunchedAt] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
          setIsAdmin(false);
          return;
        }
        setToken(sessionData.session.access_token);
        const res = await isAdminFn({
          headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
        });
        setIsAdmin(res.isAdmin);
      } catch (e) {
        console.error("admin check failed", e);
        setIsAdmin(false);
      } finally {
        setAuthChecked(true);
      }
    })();
  }, [isAdminFn]);

  const loadList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [data, launch] = await Promise.all([
        getListFn({ headers }),
        getLaunchedAtFn({ headers }),
      ]);
      setItems(data);
      setLaunchedAt(launch.launched_at);
      setNoteDrafts((prev) => {
        const next: Record<string, string> = { ...prev };
        for (const it of data) {
          if (next[it.id] === undefined) next[it.id] = it.note ?? "";
        }
        return next;
      });
    } catch (e) {
      console.error(e);
      toast.error("チェックリストの読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [getListFn, getLaunchedAtFn, token]);

  useEffect(() => {
    if (isAdmin && token) void loadList();
  }, [isAdmin, token, loadList]);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category));
    return Array.from(set).sort((a, b) => {
      // category ids: X, Y, Z, AA, BB, CC, ... — sort by length then alpha
      if (a.length !== b.length) return a.length - b.length;
      return a < b ? -1 : a > b ? 1 : 0;
    });
  }, [items]);

  const totalCount = items.length;
  const checkedCount = items.filter((i) => i.checked).length;
  const percent = totalCount === 0 ? 0 : Math.round((checkedCount / totalCount) * 100);

  const finalGateReady = useMemo(() => {
    if (items.length === 0) return false;
    return FINAL_GATE_IDS.every((id) => items.find((i) => i.id === id)?.checked);
  }, [items]);

  const visibleItems = useMemo(() => {
    return items
      .filter((i) => i.category === activeCat)
      .filter((i) => (onlyUnchecked ? !i.checked : true));
  }, [items, activeCat, onlyUnchecked]);

  async function handleToggle(item: DeployItemStatus, nextChecked: boolean) {
    if (!token) return;
    setSavingId(item.id);
    try {
      await setCheckFn({
        headers: { Authorization: `Bearer ${token}` },
        data: {
          item_id: item.id,
          checked: nextChecked,
          note: noteDrafts[item.id] ?? item.note ?? "",
        },
      });
      toast.success(nextChecked ? "チェックしました" : "チェックを外しました");
      await loadList();
    } catch (e) {
      console.error(e);
      toast.error("保存に失敗しました");
    } finally {
      setSavingId(null);
    }
  }

  async function handleSaveNote(item: DeployItemStatus) {
    if (!token) return;
    setSavingId(item.id);
    try {
      await setCheckFn({
        headers: { Authorization: `Bearer ${token}` },
        data: {
          item_id: item.id,
          checked: item.checked,
          note: noteDrafts[item.id] ?? "",
        },
      });
      toast.success("メモを保存しました");
      await loadList();
    } catch (e) {
      console.error(e);
      toast.error("メモの保存に失敗しました");
    } finally {
      setSavingId(null);
    }
  }

  async function handleReset() {
    if (!token) return;
    try {
      await resetFn({ headers: { Authorization: `Bearer ${token}` } });
      toast.success("全項目をリセットしました");
      setNoteDrafts({});
      await loadList();
    } catch (e) {
      console.error(e);
      toast.error("リセットに失敗しました");
    }
  }

  async function handleRecordLaunch() {
    if (!token) return;
    try {
      const res = await recordLaunchFn({ headers: { Authorization: `Bearer ${token}` } });
      if (res.alreadyRecorded) {
        toast.info("ローンチは既に記録済みです");
      } else {
        toast.success("🎉 ローンチを記録しました!");
      }
      await loadList();
    } catch (e) {
      console.error(e);
      toast.error("ローンチ記録に失敗しました");
    }
  }

  function handleExportCsv() {
    const header = ["id", "category", "label", "checked", "checked_by", "checked_at", "note"];
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const rows = items.map((i) =>
      [
        i.id,
        i.category,
        i.label,
        i.checked ? "true" : "false",
        i.checked_by_name ?? "",
        i.checked_at ?? "",
        i.note ?? "",
      ]
        .map((v) => escape(String(v)))
        .join(","),
    );
    const csv = "\uFEFF" + [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `deploy-checklist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!authChecked) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <ShieldAlert className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold">アクセス権がありません</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              この画面は管理者ロールを持つユーザーのみが利用できます。
            </p>
            <Button asChild className="mt-6">
              <Link to="/dashboard">ダッシュボードへ</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Button asChild variant="ghost" size="sm" className="mb-2 -ml-3">
              <Link to="/admin">
                <ArrowLeft className="mr-1 h-4 w-4" />
                管理画面へ戻る
              </Link>
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">デプロイチェックリスト</h1>
            <p className="mt-2 text-muted-foreground">
              本番ローンチに向けたインフラ・運用設定の進捗を記録します。
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleExportCsv} disabled={loading}>
              <Download className="mr-2 h-4 w-4" />
              CSV出力
            </Button>
            <Button variant="destructive" onClick={() => setResetOpen(true)} disabled={loading}>
              <RotateCcw className="mr-2 h-4 w-4" />
              全リセット
            </Button>
          </div>
        </div>

        {launchedAt && (
          <Card className="mt-6 border-primary/40 bg-primary/5 p-5">
            <div className="flex items-center gap-3">
              <PartyPopper className="h-6 w-6 text-primary" />
              <div>
                <p className="font-semibold text-primary">ローンチ記録済み</p>
                <p className="text-sm text-muted-foreground">
                  {new Date(launchedAt).toLocaleString("ja-JP")}
                </p>
              </div>
            </div>
          </Card>
        )}

        {!launchedAt && finalGateReady && (
          <Card className="mt-6 border-primary/40 bg-primary/5 p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Rocket className="h-6 w-6 text-primary" />
                <div>
                  <p className="font-semibold text-primary">ローンチ準備完了</p>
                  <p className="text-sm text-muted-foreground">
                    GG_01〜GG_06 が全てチェック済みです。本番公開を記録しましょう。
                  </p>
                </div>
              </div>
              <Button onClick={() => setLaunchOpen(true)}>
                <Rocket className="mr-2 h-4 w-4" />
                ローンチを記録
              </Button>
            </div>
          </Card>
        )}

        <Card className="mt-6 p-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">進捗</p>
              <p className="text-2xl font-bold">
                {checkedCount} / {totalCount}
                <span className="ml-2 text-base text-muted-foreground">({percent}%)</span>
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={onlyUnchecked}
                onCheckedChange={(v) => setOnlyUnchecked(Boolean(v))}
              />
              未チェックのみ表示
            </label>
          </div>
          <Progress value={percent} className="mt-4" />
        </Card>

        <Tabs value={activeCat} onValueChange={setActiveCat} className="mt-6">
          <TabsList className="flex h-auto flex-wrap justify-start gap-1">
            {categories.map((c) => (
              <TabsTrigger key={c} value={c}>
                {c}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <h2 className="mt-6 text-xl font-semibold">{CATEGORY_LABELS[activeCat] ?? activeCat}</h2>

        {loading ? (
          <div className="mt-6 flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : visibleItems.length === 0 ? (
          <Card className="mt-6 p-8 text-center text-sm text-muted-foreground">
            表示できる項目がありません。
          </Card>
        ) : (
          <div className="mt-4 space-y-3">
            {visibleItems.map((item) => (
              <Card key={item.id} className="p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id={`chk-${item.id}`}
                    checked={item.checked}
                    disabled={savingId === item.id}
                    onCheckedChange={(v) => void handleToggle(item, Boolean(v))}
                    className="mt-1"
                  />
                  <div className="min-w-0 flex-1">
                    <Label
                      htmlFor={`chk-${item.id}`}
                      className="cursor-pointer text-base font-medium"
                    >
                      <span className="mr-2 font-mono text-xs text-muted-foreground">
                        {item.id}
                      </span>
                      {item.label}
                    </Label>
                    {item.checked_by_name && item.checked_at && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {item.checked ? "✓ " : "○ "}
                        {item.checked_by_name} ・{" "}
                        {new Date(item.checked_at).toLocaleString("ja-JP")}
                      </p>
                    )}
                    <Textarea
                      placeholder="メモ（任意）"
                      value={noteDrafts[item.id] ?? ""}
                      onChange={(e) =>
                        setNoteDrafts((p) => ({ ...p, [item.id]: e.target.value }))
                      }
                      className="mt-2 min-h-[60px]"
                    />
                    <div className="mt-2 flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={
                          savingId === item.id ||
                          (noteDrafts[item.id] ?? "") === (item.note ?? "")
                        }
                        onClick={() => void handleSaveNote(item)}
                      >
                        メモを保存
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title="全項目をリセットしますか?"
        description="すべての項目を未チェック状態に戻します。履歴は残りますが、現在のチェック状態は失われます。"
        confirmLabel="リセット実行"
        destructive
        onConfirm={handleReset}
      />

      <ConfirmDialog
        open={launchOpen}
        onOpenChange={setLaunchOpen}
        title="本番ローンチを記録しますか?"
        description="現在時刻を「ローンチ日時」として system_meta に保存します。記録は1回のみで、以後 /admin に表示されます。"
        confirmLabel="ローンチを記録"
        onConfirm={handleRecordLaunch}
      />
    </main>
  );
}
